import React, { useState } from "react";
import { AlertTriangle, ChevronDown, Trash2, UsersRound, X } from "lucide-react";
import { apiFetch } from "../utils/apiClient";
import { showError } from "./ErrorPopup";

const Grouproleschema = ({
  roles,
  groupName,
  isOpen,
  onToggle,
  onRoleDeleted,
  showGroupHeader = true,
}) => {
  const [roleASupprimer, setRoleASupprimer] = useState(null);
  const [suppressionEnCours, setSuppressionEnCours] = useState(false);

  const handleDelete = async () => {
    if (!roleASupprimer) return;

    setSuppressionEnCours(true);
    try {
      const response = await apiFetch(
        `${process.env.REACT_APP_API_BASE_URL}/roles/${roleASupprimer.userId}?group=${roleASupprimer.group}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || result.message);
      }

      onRoleDeleted?.(roleASupprimer.userId, roleASupprimer.group);
      setRoleASupprimer(null);
    } catch (error) {
      console.error(error);
      showError(error);
    } finally {
      setSuppressionEnCours(false);
    }
  };

  const obtenirInitiales = (nom = "") =>
    nom
      .split(/[\s,]+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((partie) => partie.charAt(0).toUpperCase())
      .join("") || "?";

  return (
    <div
      className={
        showGroupHeader
          ? "overflow-hidden rounded-lg border border-slate-200 bg-white"
          : "bg-white"
      }
    >
      {showGroupHeader && (
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between gap-3 px-3 py-3 text-left transition-colors hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500 sm:px-4"
        aria-expanded={isOpen}
      >
        <span className="flex min-w-0 items-center gap-3">
          <span className="inline-flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-blue-50 text-blue-700">
            <UsersRound className="h-4 w-4" aria-hidden="true" />
          </span>
          <span className="min-w-0">
            <span className="block break-words text-sm font-semibold text-slate-900">
              {groupName}
            </span>
            <span className="mt-0.5 block text-xs text-slate-500">
              {roles.length} membre(s)
            </span>
          </span>
        </span>
        <ChevronDown
          className={`h-5 w-5 flex-none text-slate-500 transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
          aria-hidden="true"
        />
      </button>
      )}

      {isOpen && (
        <div
          className={`divide-y divide-slate-200 ${
            showGroupHeader ? "border-t border-slate-200" : ""
          }`}
        >
          {roles.map((role) => (
            <div
              key={`${role.group}-${role.userId}`}
              className="flex items-start gap-3 px-3 py-3.5 transition-colors hover:bg-slate-50 sm:items-center sm:px-4"
            >
              <span className="inline-flex h-10 w-10 flex-none items-center justify-center rounded-full bg-slate-100 text-xs font-bold text-slate-700">
                {obtenirInitiales(role.nom)}
              </span>

              <div className="group-member-grid min-w-0 flex-1">
                <p className="break-words text-sm font-semibold text-slate-900">
                  {role.nom || "Nom non disponible"}
                </p>
                <p className="mt-0.5 break-all text-xs text-slate-600 sm:mt-0 sm:text-sm">
                  {role.email || "Courriel non disponible"}
                </p>
              </div>

              <button
                type="button"
                onClick={() => setRoleASupprimer(role)}
                className="inline-flex h-9 w-9 flex-none items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-red-50 hover:text-red-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
                aria-label={`Retirer ${role.nom || "cet utilisateur"} du groupe ${groupName}`}
                title="Retirer du groupe"
              >
                <Trash2 className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
          ))}
        </div>
      )}

      {roleASupprimer && (
        <div
          className="fixed inset-0 z-[70] flex items-end justify-center bg-slate-950/55 p-0 backdrop-blur-[2px] sm:items-center sm:p-6"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget && !suppressionEnCours) {
              setRoleASupprimer(null);
            }
          }}
        >
          <section
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="suppression-role-title"
            className="w-full overflow-hidden rounded-t-2xl bg-white shadow-2xl sm:max-w-md sm:rounded-2xl"
          >
            <header className="flex items-start justify-between gap-4 border-b border-slate-200 px-4 py-4 sm:px-6">
              <div className="flex min-w-0 items-start gap-3">
                <span className="inline-flex h-10 w-10 flex-none items-center justify-center rounded-full bg-red-100 text-red-700">
                  <AlertTriangle className="h-5 w-5" aria-hidden="true" />
                </span>
                <div className="min-w-0">
                  <h3
                    id="suppression-role-title"
                    className="text-base font-semibold text-slate-900"
                  >
                    Retirer cet utilisateur?
                  </h3>
                  <p className="mt-1 text-sm leading-5 text-slate-600">
                    <strong>{roleASupprimer.nom}</strong> sera retiré du groupe{" "}
                    <strong>{groupName}</strong>.
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setRoleASupprimer(null)}
                disabled={suppressionEnCours}
                className="inline-flex h-9 w-9 flex-none items-center justify-center rounded-lg text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
                aria-label="Fermer"
              >
                <X className="h-5 w-5" aria-hidden="true" />
              </button>
            </header>

            <footer className="flex flex-col-reverse gap-2 bg-slate-50 px-4 py-4 sm:flex-row sm:justify-end sm:px-6">
              <button
                type="button"
                onClick={() => setRoleASupprimer(null)}
                disabled={suppressionEnCours}
                className="inline-flex min-h-11 w-full items-center justify-center rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={suppressionEnCours}
                className="inline-flex min-h-11 w-full items-center justify-center rounded-lg border border-red-700 bg-red-700 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-red-800 disabled:cursor-wait disabled:opacity-60 sm:w-auto"
              >
                {suppressionEnCours ? "Suppression..." : "Retirer du groupe"}
              </button>
            </footer>
          </section>
        </div>
      )}
    </div>
  );
};

export default Grouproleschema;
